/**
 * Combined GCC report (WI 2486): one HTML file with the GCC dashboard and every
 * TN analysis of the design, so a presenter can walk a client through all
 * scenarios without switching files.
 *
 * Each view is a full single-page document with its own global scripts and CSS,
 * so the report does not merge them: it is a shell with a view switcher, and each
 * view runs in its own iframe. The documents are embedded as JSON strings and only
 * loaded into an iframe (srcdoc) the first time their tab opens, so a report with
 * several ~1 MB TN analyses still opens quickly.
 */
import type { GccCombinableScenario, Project } from '@shared/types'
import { GCC_EXECUTIVE_TOKENS, gccExecutivePaletteCss } from '@tn/shared'
import { gccTnAnalysisLabel } from './gcc-meta'

/** A linked TN analysis, fetched once for the bundle and the combined report. */
export interface GccTnAnalysisExport {
  id: string
  /** Scenario label shown on the report tab, e.g. "Moderate Outsourcing (Medium)". */
  label: string
  /** File name in the bundle's tn-analyses/ folder, without extension. */
  fileBase: string
  /** The dashboard HTML, or null when it could not be fetched. */
  html: string | null
  /** Position in the presenting order: Retained, Low, Medium, High, then Combined views. */
  rank: number
}

type TnProjectMeta = Pick<Project, 'id' | 'gccCombined' | 'gccScenario'>

const SCENARIO_RANK: Record<string, number> = { Retained: 0, Low: 1, Medium: 2, High: 3 }
const COMBINED_RANK: Record<GccCombinableScenario, number> = { Low: 4, Medium: 5 }
const OTHER_RANK = 9

function fileBaseOf(id: string, idx: number, tnProject: TnProjectMeta | undefined): string {
  const combined = tnProject?.gccCombined
  const scenario = tnProject?.gccScenario
  if (combined) return `tn-combined-retained-${combined.offshoreScenario.toLowerCase()}`
  if (scenario) return `tn-analysis-${idx + 1}-${scenario.toLowerCase()}-${id}`
  return `tn-analysis-${idx + 1}-${id}`
}

function rankOf(tnProject: TnProjectMeta | undefined): number {
  if (tnProject?.gccCombined) return COMBINED_RANK[tnProject.gccCombined.offshoreScenario]
  return SCENARIO_RANK[tnProject?.gccScenario ?? ''] ?? OTHER_RANK
}

/**
 * Fetch every TN analysis linked to the design, in derivedTnProjectIds order.
 * A failed fetch is kept with html: null so the bundle and the report can say so.
 * Without a resolver (or with no linked analyses) the list is empty.
 */
export async function collectTnAnalyses(
  project: Pick<Project, 'derivedTnProjectIds'>,
  getTnHtml?: (projectId: string) => Promise<string>,
  tnProjects: TnProjectMeta[] = [],
): Promise<GccTnAnalysisExport[]> {
  if (!getTnHtml) return []
  return Promise.all((project.derivedTnProjectIds ?? []).map(async (id, idx) => {
    const tnProject = tnProjects.find(p => p.id === id)
    let html: string | null
    try {
      html = await getTnHtml(id)
    } catch {
      html = null
    }
    return { id, label: gccTnAnalysisLabel(tnProject, idx), fileBase: fileBaseOf(id, idx, tnProject), html, rank: rankOf(tnProject) }
  }))
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** A document as a JSON string literal that cannot close its <script> block. */
function embedDocument(html: string): string {
  return JSON.stringify(html).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
}

// Allows what the embedded views need: the views inherit this policy on top of their own.
// The TN views come from injectDashboardData() output, which (bug 2774) no longer ships the
// remote Plotly script tag, so no external host needs allowlisting here.
const REPORT_CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; connect-src 'none'"

const REPORT_CSS = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{${GCC_EXECUTIVE_TOKENS}}
html,body{height:100%}
body{display:flex;flex-direction:column;font-family:'Graphik','Inter','Segoe UI Variable Text','Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;background:var(--bg);color:var(--ink);font-size:14px;-webkit-font-smoothing:antialiased}
button{font:inherit}
.rb{flex:none;color:#fff;background:linear-gradient(120deg,var(--night) 0%,var(--night2) 58%,var(--night3) 100%);padding:0 32px;border-bottom:1px solid rgba(255,255,255,.08)}
.rb-top{display:flex;align-items:center;gap:24px;padding:12px 0 4px;flex-wrap:wrap}
.wordmark{display:flex;flex-direction:column;line-height:1}
.wordmark b{font-size:15px;font-weight:700;letter-spacing:.06em;white-space:nowrap}
.wordmark span{font-size:9px;font-weight:600;letter-spacing:.32em;text-transform:uppercase;opacity:.7;margin-top:5px}
.rb-client{margin-left:auto;text-align:right;font-size:13px;font-weight:600}
.rb-client small{display:block;font-size:11px;font-weight:400;color:rgba(255,255,255,.6);margin-top:2px}
.rb-tabs{display:flex;gap:2px;overflow-x:auto;scrollbar-width:none}
.rb-tabs::-webkit-scrollbar{display:none}
.rb-tab{position:relative;border:none;cursor:pointer;padding:12px 14px 11px;font-size:13px;font-weight:500;color:rgba(255,255,255,.62);background:transparent;white-space:nowrap}
.rb-tab:hover{color:#fff}
.rb-tab[aria-selected="true"]{color:#fff;font-weight:600}
.rb-tab[aria-selected="true"]::after{content:'';position:absolute;left:10px;right:10px;bottom:0;height:2px;border-radius:2px 2px 0 0;background:linear-gradient(90deg,var(--brand),var(--brand-on-dark));box-shadow:0 0 12px var(--brand)}
.rb-tab:focus-visible{outline:2px solid #fff;outline-offset:-4px;border-radius:6px}
.rb-sep{flex:none;width:1px;margin:10px 8px;background:rgba(255,255,255,.16)}
main{flex:1;min-height:0;position:relative}
.view{position:absolute;inset:0}
.view[hidden]{display:none}
.view iframe{display:block;width:100%;height:100%;border:0;background:#fff}
.missing{max-width:560px;margin:64px auto;padding:28px;background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);box-shadow:var(--sh-1)}
.missing h2{font-size:17px;font-weight:600;margin-bottom:8px}
.missing p{font-size:13px;color:var(--ink2);line-height:1.55}
@media print{.rb{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
`

// ES5, like the dashboards it hosts. Opens the tab in the URL hash (#view-<n>) or the first one.
const REPORT_JS = `
(function(){
  var tabs = document.querySelectorAll('.rb-tab');
  function show(i) {
    for (var t = 0; t < tabs.length; t++) {
      var on = t === i;
      tabs[t].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[t].tabIndex = on ? 0 : -1;
      document.getElementById('view-' + t).hidden = !on;
    }
    var panel = document.getElementById('view-' + i);
    var src = document.getElementById('doc-' + i);
    if (src && !panel.querySelector('iframe')) {
      var frame = document.createElement('iframe');
      frame.title = tabs[i].textContent;
      frame.srcdoc = JSON.parse(src.textContent);
      panel.appendChild(frame);
    }
    try { history.replaceState(null, '', '#view-' + i); } catch (e) {}
  }
  for (var t = 0; t < tabs.length; t++) {
    (function(i){
      tabs[i].addEventListener('click', function(){ show(i); });
      tabs[i].addEventListener('keydown', function(e){
        var n = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null;
        if (n === null) return;
        e.preventDefault();
        n = (n + tabs.length) % tabs.length;
        show(n);
        tabs[n].focus();
      });
    })(t);
  }
  var m = /^#view-(\\d+)$/.exec(location.hash);
  var start = m ? +m[1] : 0;
  show(start < tabs.length ? start : 0);
})();
`

/**
 * Build the combined report: a shell in the GCC executive look (client brand
 * palette) with a GCC Design view first, then one view per TN analysis in
 * presenting order. An analysis that could not be fetched keeps its tab with a note.
 *
 * @param gccHtml   The exported GCC dashboard (generateGccHtml)
 * @param project   The GCC design (client, industry, branding)
 * @param analyses  The linked TN analyses (collectTnAnalyses)
 */
export function generateGccCombinedReport(
  gccHtml: string,
  project: Pick<Project, 'client' | 'industry' | 'branding'>,
  analyses: GccTnAnalysisExport[],
): string {
  const ordered = analyses.map((a, i) => ({ a, i })).sort((x, y) => x.a.rank - y.a.rank || x.i - y.i).map(x => x.a)
  const views = [{ label: 'GCC Design', html: gccHtml as string | null }, ...ordered]
  const client = project.client ?? ''
  const palette = gccExecutivePaletteCss(project.branding?.primaryColor, project.branding?.accentColor)

  const tabs = views.map((v, i) =>
    `<button class="rb-tab" role="tab" id="tab-${i}" aria-controls="view-${i}" aria-selected="false" tabindex="-1">${escapeHtml(v.label)}</button>` +
    (i === 0 && views.length > 1 ? '<span class="rb-sep" aria-hidden="true"></span>' : ''),
  ).join('')
  const panels = views.map((v, i) =>
    `<section class="view" id="view-${i}" role="tabpanel" aria-labelledby="tab-${i}" hidden>` +
    (v.html === null
      ? `<div class="missing"><h2>${escapeHtml(v.label)}</h2><p>This TN analysis could not be included when the report was built. Export the report again from Talent Navigator to add it.</p></div>`
      : '') +
    '</section>',
  ).join('\n')
  const docs = views.map((v, i) =>
    v.html === null ? '' : `<script type="application/json" id="doc-${i}">${embedDocument(v.html)}</script>`,
  ).join('\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="Content-Security-Policy" content="${REPORT_CSP}">
  <title>${escapeHtml(client)} — Global Capability Center Report</title>
  <style>${REPORT_CSS}
${palette}
  </style>
</head>
<body>
  <header class="rb">
    <div class="rb-top">
      <div class="wordmark"><b>Global Capability Center</b><span>Combined report</span></div>
      <div class="rb-client">${escapeHtml(client)}${project.industry ? `<small>${escapeHtml(project.industry)}</small>` : ''}</div>
    </div>
    <nav class="rb-tabs" role="tablist" aria-label="Report views">${tabs}</nav>
  </header>
  <main>
${panels}
  </main>
${docs}
  <script>${REPORT_JS}</script>
</body>
</html>`
}
